import {
	normalizeAllowedMcpTools,
	normalizeBlockedInternalMcpTools,
} from "./mcp-tool-whitelist"

// Who may call a connector tool. "agent" is the customer-facing agent plus the
// team's AI (Assistant and drafts); "team" is the team's AI only.
export const TOOL_ACCESS_LEVELS = ["agent", "team", "off"] as const
export type ToolAccess = (typeof TOOL_ACCESS_LEVELS)[number]

export interface ToolAccessLists {
	allowedMcpTools: readonly string[]
	blockedInternalMcpTools: readonly string[]
}

// Allowed for the agent but blocked internally has no level of its own; it
// predates the single control and shows as mixed until someone picks a level.
export function toolAccessOf(
	tool: string,
	lists: ToolAccessLists,
): ToolAccess | null {
	const allowed = lists.allowedMcpTools.includes(tool)
	const blocked = lists.blockedInternalMcpTools.includes(tool)
	if (allowed && blocked) return null
	if (allowed) return "agent"
	if (blocked) return "off"
	return "team"
}

// The level every tool shares, or null when they differ (or there are none).
export function sharedToolAccess(
	tools: readonly string[],
	lists: ToolAccessLists,
): ToolAccess | null {
	let shared: ToolAccess | null = null
	for (const tool of tools) {
		const access = toolAccessOf(tool, lists)
		if (access === null) return null
		if (shared !== null && shared !== access) return null
		shared = access
	}
	return shared
}

export function withToolAccess(
	tools: readonly string[],
	access: ToolAccess,
	lists: ToolAccessLists,
): { allowedMcpTools: string[]; blockedInternalMcpTools: string[] } {
	const targets = new Set(tools)
	const allowed = lists.allowedMcpTools.filter((tool) => !targets.has(tool))
	const blocked = lists.blockedInternalMcpTools.filter(
		(tool) => !targets.has(tool),
	)
	if (access === "agent") allowed.push(...targets)
	if (access === "off") blocked.push(...targets)
	return {
		allowedMcpTools: normalizeAllowedMcpTools(allowed),
		blockedInternalMcpTools: normalizeBlockedInternalMcpTools(blocked),
	}
}

const READ_ONLY_NAME =
	/^(get|list|search|find|read|fetch|retrieve|lookup|look_up|query|describe|show|count|check|view)(_|-|[A-Z]|$)/

// Servers that annotate their tools decide; the rest are read from the name,
// and anything unrecognised counts as a change so it starts in the stricter group.
export function isReadOnlyTool(tool: {
	name: string
	readOnly?: boolean | null
}): boolean {
	if (typeof tool.readOnly === "boolean") return tool.readOnly
	return READ_ONLY_NAME.test(tool.name)
}

// "list_invoices" and "listInvoices" both read as "List invoices".
export function humanizeToolName(name: string): string {
	const words = name
		.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
		.split(/[\s_\-.]+/)
		.filter(Boolean)
		.map((word) => word.toLowerCase())
	if (words.length === 0) return name
	const sentence = words.join(" ")
	return sentence.charAt(0).toUpperCase() + sentence.slice(1)
}

// A server that rejects the credentials says so in the status or the wording;
// that is the one failure signing in again fixes.
const SIGNED_OUT_PATTERN =
	/\b(401|403)\b|unauthori[sz]ed|forbidden|authenticat|missing_api_key|invalid[_ ](api[_ ])?key|expired|revoked|invalid_token|invalid_client|token endpoint/i

export function looksSignedOut(message: string | null | undefined): boolean {
	return !!message && SIGNED_OUT_PATTERN.test(message)
}
